import { useState, useEffect, useCallback, useRef } from 'react';
import '@/App.css';
import axios from 'axios';
import { Toaster, toast } from 'sonner';
import { AppHeader } from './components/AppHeader';
import { SearchForm } from './components/SearchForm';
import { FuelFilters } from './components/FuelFilters';
import { SortControls } from './components/SortControls';
import { StationList } from './components/StationList';
import { LoadingState } from './components/LoadingState';
import { WelcomeScreen } from './components/WelcomeScreen';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const ITEMS_PER_PAGE = 10;

function App() {
  const [fuels, setFuels] = useState([]);
  const [selectedFuels, setSelectedFuels] = useState([]);
  const [stations, setStations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [sortBy, setSortBy] = useState('price');
  const [hasDistance, setHasDistance] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [totalStations, setTotalStations] = useState(0);
  
  // Store search params for pagination
  const searchParamsRef = useRef({ type: null, query: null, lat: null, lon: null });
  
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('darkMode');
      if (saved !== null) return saved === 'true';
      return window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    return false;
  });

  // Apply dark mode class to document
  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    localStorage.setItem('darkMode', darkMode.toString());
  }, [darkMode]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prev => !prev);
  }, []);

  // Fetch fuel types on mount
  useEffect(() => {
    const fetchFuels = async () => {
      try {
        const response = await axios.get(`${API}/fuels`);
        setFuels(response.data);
      } catch (error) {
        console.error('Error fetching fuels:', error);
        // Use default fuels
        setFuels([
          { id: 1, name: 'Gazole', short_name: 'Gazole', type: 'D', picto: 'B7' },
          { id: 2, name: 'SP95', short_name: 'SP95', type: 'E', picto: 'E5' },
          { id: 3, name: 'E85', short_name: 'E85', type: 'E', picto: 'E85' },
          { id: 4, name: 'GPLc', short_name: 'GPLc', type: 'G', picto: 'LPG' },
          { id: 5, name: 'E10', short_name: 'E10', type: 'E', picto: 'E10' },
          { id: 6, name: 'SP98', short_name: 'SP98', type: 'E', picto: 'E5' }
        ]);
      }
    };
    fetchFuels();
  }, []);

  const handleSearch = useCallback(async (query, offset = 0) => {
    if (offset === 0) {
      setIsLoading(true);
      setStations([]);
    } else {
      setIsLoadingMore(true);
    }
    setHasSearched(true);
    setHasDistance(false);
    
    // Store search params for load more
    searchParamsRef.current = { type: 'search', query, lat: null, lon: null };
    
    try {
      const params = new URLSearchParams({ 
        q: query, 
        offset: offset.toString(),
        limit: ITEMS_PER_PAGE.toString()
      });
      if (selectedFuels.length > 0) {
        params.append('fuels', selectedFuels.join(','));
      }
      
      const response = await axios.get(`${API}/stations/search?${params.toString()}`);
      const newStations = response.data.stations || [];
      
      if (offset === 0) {
        setStations(newStations);
        if (newStations.length === 0) {
          toast.info('Aucune station trouvée pour cette recherche');
        } else {
          toast.success(`${response.data.total || newStations.length} station(s) trouvée(s)`);
        }
      } else {
        setStations(prev => [...prev, ...newStations]);
      }
      
      setHasMore(response.data.has_more || false);
      setCurrentOffset(offset + newStations.length);
      setTotalStations(response.data.total || 0);
      
    } catch (error) {
      console.error('Search error:', error);
      toast.error('Erreur lors de la recherche. Veuillez réessayer.');
      if (offset === 0) setStations([]);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [selectedFuels]);

  const handleGeolocate = useCallback(async (lat, lon, offset = 0) => {
    if (offset === 0) {
      setIsLoading(true);
      setStations([]);
    } else {
      setIsLoadingMore(true);
    }
    setHasSearched(true);
    setHasDistance(true);
    if (offset === 0) setSortBy('distance');
    
    // Store search params for load more
    searchParamsRef.current = { type: 'geo', query: null, lat, lon };
    
    try {
      const params = new URLSearchParams({ 
        lat: lat.toString(), 
        lon: lon.toString(),
        offset: offset.toString(),
        limit: ITEMS_PER_PAGE.toString()
      });
      if (selectedFuels.length > 0) {
        params.append('fuels', selectedFuels.join(','));
      }
      
      const response = await axios.get(`${API}/stations/around?${params.toString()}`);
      const newStations = response.data.stations || [];
      
      if (offset === 0) {
        setStations(newStations);
        if (newStations.length === 0) {
          toast.info('Aucune station trouvée à proximité');
        } else {
          toast.success(`${response.data.total || newStations.length} station(s) trouvée(s) à proximité`);
        }
      } else {
        setStations(prev => [...prev, ...newStations]);
      }
      
      setHasMore(response.data.has_more || false);
      setCurrentOffset(offset + newStations.length);
      setTotalStations(response.data.total || 0);
      
    } catch (error) {
      console.error('Geolocation search error:', error);
      toast.error('Erreur lors de la recherche. Veuillez réessayer.');
      if (offset === 0) setStations([]);
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [selectedFuels]);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    
    const { type, query, lat, lon } = searchParamsRef.current;
    if (type === 'search' && query) {
      handleSearch(query, currentOffset);
    } else if (type === 'geo' && lat && lon) {
      handleGeolocate(lat, lon, currentOffset);
    }
  }, [isLoadingMore, hasMore, currentOffset, handleSearch, handleGeolocate]);

  const handleToggleFuel = useCallback((fuelId) => {
    setSelectedFuels(prev => 
      prev.includes(fuelId) 
        ? prev.filter(id => id !== fuelId)
        : [...prev, fuelId]
    );
  }, []);

  const handleSortChange = useCallback((newSortBy) => {
    setSortBy(newSortBy);
  }, []);

  return (
    <div className={`app-container ${darkMode ? 'dark' : ''}`} data-testid="app-container">
      <Toaster 
        position="top-center" 
        theme={darkMode ? 'dark' : 'light'}
        toastOptions={{
          style: {
            background: darkMode ? '#fff' : '#000',
            color: darkMode ? '#000' : '#fff',
            border: '2px solid',
            borderColor: darkMode ? '#fff' : '#000',
            borderRadius: '0',
            fontFamily: 'Manrope, sans-serif',
          },
        }}
      />
      
      <AppHeader darkMode={darkMode} onToggleDarkMode={toggleDarkMode} />
      
      <SearchForm 
        onSearch={handleSearch}
        onGeolocate={handleGeolocate}
        isLoading={isLoading}
      />

      <FuelFilters 
        fuels={fuels}
        selectedFuels={selectedFuels}
        onToggleFuel={handleToggleFuel}
      />

      {hasSearched && (
        <SortControls 
          sortBy={sortBy}
          onSortChange={handleSortChange}
          hasDistance={hasDistance}
        />
      )}

      <main className="pb-8">
        {isLoading ? (
          <LoadingState />
        ) : hasSearched ? (
          <StationList 
            stations={stations}
            selectedFuels={selectedFuels}
            sortBy={sortBy}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            onLoadMore={loadMore}
            totalStations={totalStations}
          />
        ) : (
          <WelcomeScreen />
        )}
      </main>
    </div>
  );
}

export default App;
